import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCompaniesStore } from '@/stores/companies';
import { useEmployeesStore } from '@/stores/employees';

const EXCLUDED_EMPLOYEE_STATUSES = new Set([
  'Resigned',
  'Terminated',
  'Hold',
  'On Hold',
  'Cancelled'
]);

export function useEmployeeTreeSelection() {
  const companiesStore = useCompaniesStore();
  const employeesStore = useEmployeesStore();
  const { companiesList } = storeToRefs(companiesStore);
  const { employeesList } = storeToRefs(employeesStore);
  const selectionKeys = ref({});

  const eligibleEmployees = computed(() => employeesList.value.filter(
    employee => !EXCLUDED_EMPLOYEE_STATUSES.has(employee.employee_status)
  ));

  const employeesByCompany = computed(() => {
    const groupedEmployees = new Map();
    eligibleEmployees.value.forEach(employee => {
      const companyId = employee.employee_company_id;
      if (!groupedEmployees.has(companyId)) groupedEmployees.set(companyId, []);
      groupedEmployees.get(companyId).push(employee);
    });
    return groupedEmployees;
  });

  const treeOptions = computed(() => companiesList.value
    .map(company => {
      const employees = employeesByCompany.value.get(company.company_id) || [];
      return {
        key: `company:${company.company_id}`,
        label: company.company_name,
        styleClass: 'employee-tree-company-node',
        style: {
          '--employee-tree-company-count': `"(${employees.length})"`
        },
        data: { type: 'company', companyId: company.company_id },
        children: employees.map(employee => ({
          key: `employee:${employee.employee_id}`,
          label: employee.employee_name,
          data: {
            type: 'employee',
            employeeId: employee.employee_id,
            companyId: company.company_id
          }
        }))
      };
    })
    .filter(companyNode => companyNode.children.length > 0));

  const selectedEmployeeIds = computed(() => {
    const employeeIds = new Set();
    treeOptions.value.forEach(companyNode => {
      companyNode.children.forEach(employeeNode => {
        if (selectionKeys.value[employeeNode.key]?.checked) {
          employeeIds.add(employeeNode.data.employeeId);
        }
      });
    });
    return Array.from(employeeIds).sort((firstId, secondId) => firstId - secondId);
  });

  // TreeSelect calculates parent checkbox state against the currently filtered
  // children. Recalculate it against the complete company tree so selecting one
  // filtered employee cannot make the whole company appear selected.
  watch([selectionKeys, treeOptions], ([currentSelectionKeys]) => {
    const normalizedSelectionKeys = { ...currentSelectionKeys };
    let selectionChanged = false;

    treeOptions.value.forEach(companyNode => {
      const selectedChildrenCount = companyNode.children.reduce((count, employeeNode) => (
        count + (currentSelectionKeys[employeeNode.key]?.checked ? 1 : 0)
      ), 0);
      const currentCompanyState = currentSelectionKeys[companyNode.key];

      if (selectedChildrenCount === 0) {
        if (currentCompanyState) {
          delete normalizedSelectionKeys[companyNode.key];
          selectionChanged = true;
        }
        return;
      }

      const expectedCompanyState = {
        checked: selectedChildrenCount === companyNode.children.length,
        partialChecked: selectedChildrenCount < companyNode.children.length
      };

      if (
        currentCompanyState?.checked !== expectedCompanyState.checked
        || currentCompanyState?.partialChecked !== expectedCompanyState.partialChecked
      ) {
        normalizedSelectionKeys[companyNode.key] = expectedCompanyState;
        selectionChanged = true;
      }
    });

    if (selectionChanged) {
      selectionKeys.value = normalizedSelectionKeys;
    }
  }, { flush: 'sync' });

  const selectedEmployees = computed(() => {
    const selectedIds = new Set(selectedEmployeeIds.value);
    return eligibleEmployees.value.filter(employee => selectedIds.has(employee.employee_id));
  });

  const selectedEmployeeFirstNames = computed(() => selectedEmployees.value.map(employee => (
    employee.employee_name?.trim().split(/\s+/)[0] || `#${employee.employee_id}`
  )));

  const selectionLoading = computed(() => (
    employeesStore.employeesLoading || companiesStore.companiesLoading
  ));

  const clearSelection = () => {
    selectionKeys.value = {};
  };

  const setSelectedEmployeeIds = employeeIds => {
    const requestedEmployeeIds = new Set(employeeIds);
    const matchedEmployeeIds = new Set();
    const nextSelectionKeys = {};

    treeOptions.value.forEach(companyNode => {
      let selectedChildrenCount = 0;
      companyNode.children.forEach(employeeNode => {
        const employeeId = employeeNode.data.employeeId;
        if (requestedEmployeeIds.has(employeeId)) {
          matchedEmployeeIds.add(employeeId);
          selectedChildrenCount += 1;
          nextSelectionKeys[employeeNode.key] = { checked: true, partialChecked: false };
        }
      });

      if (selectedChildrenCount > 0) {
        nextSelectionKeys[companyNode.key] = {
          checked: selectedChildrenCount === companyNode.children.length,
          partialChecked: selectedChildrenCount < companyNode.children.length
        };
      }
    });

    selectionKeys.value = nextSelectionKeys;
    return employeeIds.filter(employeeId => !matchedEmployeeIds.has(employeeId));
  };

  const ensureSelectionData = () => Promise.all([
    companiesStore.ensureCompanies(),
    employeesStore.ensureEmployees()
  ]);

  return {
    selectionKeys,
    treeOptions,
    eligibleEmployees,
    selectedEmployeeIds,
    selectedEmployees,
    selectedEmployeeFirstNames,
    selectionLoading,
    clearSelection,
    setSelectedEmployeeIds,
    ensureSelectionData
  };
}
